import type { BlogArticle, ExploreFeaturePage } from './types'
import { absoluteUrl, DEFAULT_OG_IMAGE, SITE_NAME, SITE_ORIGIN } from './site'
import { BLOG_ARTICLES, EXPLORE_FEATURES } from './content'
import { LANDING_DOMAINS } from '../constants/landingCatalog'
import { LANDING_FAQ_SECTIONS } from '../constants/landingFaq'

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function renderList(items: string[]): string {
  return `<ul>${items.map((item) => `<li>${escapeHtml(item)}</li>`).join('')}</ul>`
}

function renderRelated(links: Array<{ path: string; label: string }>): string {
  return `<ul>${links
    .map((link) => `<li><a href="${escapeHtml(link.path)}">${escapeHtml(link.label)}</a></li>`)
    .join('')}</ul>`
}

export function renderExploreFeatureBody(page: ExploreFeaturePage): string {
  return `
<article>
  <h1>${escapeHtml(page.h1)}</h1>
  <p>${escapeHtml(page.intro)}</p>
  <h2>${escapeHtml(page.problemTitle)}</h2>
  <p>${escapeHtml(page.problem)}</p>
  <h2>${escapeHtml(page.allowsTitle)}</h2>
  ${renderList(page.allows)}
  <h2>${escapeHtml(page.howTitle)}</h2>
  ${renderList(page.howSteps)}
  <h2>${escapeHtml(page.examplesTitle)}</h2>
  ${renderList(page.examples)}
  <h2>${escapeHtml(page.relatedTitle)}</h2>
  ${renderRelated(page.related)}
  <p><a href="/">${escapeHtml('Empieza a usar Escribonauta gratis')}</a></p>
</article>`.trim()
}

export function renderBlogArticleBody(article: BlogArticle): string {
  const sections = article.sections
    .map(
      (section) => `
  <h2>${escapeHtml(section.heading)}</h2>
  ${section.paragraphs.map((paragraph) => `<p>${escapeHtml(paragraph)}</p>`).join('\n  ')}`,
    )
    .join('\n')

  return `
<article>
  <h1>${escapeHtml(article.title)}</h1>
  <p>${escapeHtml(article.summary)}</p>
  ${sections}
  <h2>Explora Escribonauta</h2>
  ${renderRelated(article.relatedFeatures)}
  <p><a href="/">${escapeHtml('Empieza a usar Escribonauta gratis')}</a></p>
</article>`.trim()
}

export function renderBlogIndexBody(): string {
  const items = BLOG_ARTICLES.map(
    (article) =>
      `<li><a href="${escapeHtml(article.path)}"><strong>${escapeHtml(article.title)}</strong></a> — ${escapeHtml(article.summary)}</li>`,
  ).join('')

  return `
<article>
  <h1>Blog de Escribonauta</h1>
  <p>Artículos prácticos para escritores que quieren organizar, planificar y terminar sus novelas, con la tranquilidad de que ninguna IA tiene acceso a su obra.</p>
  <ul>${items}</ul>
</article>`.trim()
}

export function renderFaqBody(): string {
  const sections = LANDING_FAQ_SECTIONS.map((section) => {
    const entries = section.items
      .map(
        (item) =>
          `<section><h3>${escapeHtml(item.question)}</h3><p>${escapeHtml(item.answer)}</p></section>`,
      )
      .join('')
    return `<section><h2>${escapeHtml(section.title)}</h2>${entries}</section>`
  }).join('')

  return `
<article>
  <h1>Preguntas frecuentes</h1>
  <p>Respuestas rápidas sobre límites, privacidad y cómo funciona Escribonauta.</p>
  ${sections}
</article>`.trim()
}

export function faqPageJsonLd(items: Array<{ question: string; answer: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  }
}

export function renderFeaturesCatalogBody(): string {
  const domains = LANDING_DOMAINS.map((domain) => {
    const cards = domain.catalogFeatures
      .map(
        (feature) =>
          `<li><h4>${escapeHtml(feature.title)}</h4><p>${escapeHtml(feature.description)}</p></li>`,
      )
      .join('')
    return `<section id="${escapeHtml(domain.anchor)}"><h2>${escapeHtml(domain.kicker)}</h2><p>${escapeHtml(domain.explorationText)}</p><ul>${cards}</ul></section>`
  }).join('')

  return `
<article>
  <h1>Lo que puedes hacer con tu historia</h1>
  <p>Herramientas para desarrollar tu historia y para compartirla con otros escritores.</p>
  ${domains}
</article>`.trim()
}

export function renderHomeBody(): string {
  const exploreLinks = EXPLORE_FEATURES.map(
    (page) => `<li><a href="${escapeHtml(page.path)}">${escapeHtml(page.navLabel)}</a></li>`,
  ).join('')

  const heroCopy = LANDING_DOMAINS.map(
    (domain) =>
      `<p>Tu historia merece ser ${escapeHtml(domain.heroWord)}. ${escapeHtml(domain.heroDescription)}</p>`,
  ).join('\n  ')

  const domains = LANDING_DOMAINS.map((domain) => {
    const groups = domain.groups
      .map((group) => {
        const cards = group.features
          .map(
            (feature) =>
              `<li><h4>${escapeHtml(feature.title)}</h4><p>${escapeHtml(feature.description)}</p></li>`,
          )
          .join('')
        return `<h3>${escapeHtml(group.label)}</h3><ul>${cards}</ul>`
      })
      .join('')
    return `<section id="${escapeHtml(domain.anchor)}"><h2>${escapeHtml(domain.kicker)}</h2><p>${escapeHtml(domain.explorationText)}</p>${groups}</section>`
  }).join('')

  return `
<article>
  <h1>Escribonauta</h1>
  ${heroCopy}
  <h2>¿Qué vienes a hacer hoy?</h2>
  <p>Elige un camino y te mostramos solo lo que te interesa.</p>
  ${domains}
  <h2>Tu historia es solo tuya</h2>
  <p>Ninguna inteligencia artificial tiene acceso a tu obra. Solo el escritor puede leerla y editarla.</p>
  <h2>Explora las funcionalidades</h2>
  <ul>${exploreLinks}</ul>
  <p><a href="/funcionalidades">Ver más funcionalidades</a></p>
  <p><a href="/blog">Lee el blog</a></p>
  <p><a href="/">Empieza a usar Escribonauta gratis</a></p>
</article>`.trim()
}

export function softwareApplicationJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: SITE_NAME,
    url: SITE_ORIGIN,
    applicationCategory: 'WritingApplication',
    operatingSystem: 'Web',
    inLanguage: 'es',
    description:
      'Escribonauta te ayuda a organizar, escribir y gestionar tus proyectos literarios. Ninguna inteligencia artificial tiene acceso a tu historia: solo el escritor puede leerla y editarla.',
    featureList: [
      'Editor de escritura creativa',
      'Planificación de novelas y tramas',
      'Sin acceso de inteligencia artificial a tus historias',
    ],
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'CLP',
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_ORIGIN,
    },
  }
}

export function websiteJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_ORIGIN,
    inLanguage: 'es',
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_ORIGIN,
    },
  }
}

export function organizationJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_ORIGIN,
    logo: DEFAULT_OG_IMAGE,
  }
}

export function blogPostingJsonLd(article: BlogArticle) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: article.title,
    description: article.metaDescription,
    datePublished: article.publishedAt,
    mainEntityOfPage: absoluteUrl(article.path),
    author: {
      '@type': 'Organization',
      name: SITE_NAME,
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_ORIGIN,
    },
    image: DEFAULT_OG_IMAGE,
    inLanguage: 'es',
  }
}
